import html2canvas from "html2canvas";

/**
 * Clones the receipt element, fixes html2canvas-incompatible CSS calc() values,
 * and returns a rendered HTMLCanvasElement.
 */
export const captureReceiptCanvas = async (el: HTMLDivElement): Promise<HTMLCanvasElement> => {
  // html2canvas parses the backgroundColor option with its own color parser,
  // which throws on CSS custom properties — resolve the token to a concrete
  // color first (also keeps the capture theme-aware).
  const surfaceColor =
    getComputedStyle(document.documentElement).getPropertyValue("--app-base-white").trim() || "#ffffff";

  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.top = "-99999px";
  wrapper.style.left = "0";
  wrapper.style.width = `${el.offsetWidth}px`;
  wrapper.style.backgroundColor = surfaceColor;
  wrapper.style.padding = "24px 0";

  const clone = el.cloneNode(true) as HTMLElement;
  clone.style.width = "100%";
  clone.style.height = "auto";
  clone.style.maxHeight = "none";
  clone.style.overflow = "visible";
  clone.style.flex = "none";
  clone.style.position = "relative";

  const inner = clone.querySelector("[class*='receiptContent']") as HTMLElement | null;
  if (inner) {
    inner.style.overflow = "visible";
    inner.style.height = "auto";
    inner.style.maxHeight = "none";
    inner.style.flex = "none";
  }

  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  // html2canvas does not reliably resolve calc() in background-position.
  // The receiptContainer uses radial-gradients with `0 -11px` (top) and
  // `0 calc(100% + 11px)` (bottom) to create the scalloped edge effect.
  // We replace those with computed pixel values so the waves render correctly.
  const cloneHeight = clone.offsetHeight;
  // calc(100% + 11px) with background-size 22px resolves to: (cloneHeight - 22) * 1 + 11 = cloneHeight - 11
  const bottomPosY = cloneHeight - 11;
  clone.style.backgroundPosition = [
    ...Array(10).fill("0px -11px"),
    ...Array(10).fill(`0px ${bottomPosY}px`),
  ].join(", ");

  const canvas = await html2canvas(wrapper, {
    backgroundColor: surfaceColor,
    useCORS: true,
    allowTaint: true,
    width: el.offsetWidth,
    scrollX: 0,
    scrollY: 0,
  });

  document.body.removeChild(wrapper);

  return canvas;
};
